import { isEmpty } from 'lodash'
import { convertMoney } from '../../currency/utils'
import {
  type GetOrderChangePolicyListQuery,
  useGetOrderChangePolicyListQuery,
} from '../../../../api'
import { type EagerResponse, useApolloRequestWrapper } from '../../../api/useApolloRequestWrapper'
import {
  type OrderChangePolicyList,
  type GetOrderChangePolicyListRequestParams,
  type OrderCancelPolicy,
  type OrderReschedulePolicy,
} from './types'

function mapResponse(data: GetOrderChangePolicyListQuery): OrderChangePolicyList {
  const policyList =
    data.instacart_enterprise_frigate_fulfillment_v1_DashboardOrderService_GetOrderCancellationPolicyList

  if (!policyList) {
    return {
      orderChangeToken: '',
      cancellationPolicies: [],
      reschedulePolicies: [],
    }
  }

  return {
    orderChangeToken: policyList.orderChangeToken!,
    cancellationPolicies: (policyList.cancellationPolicies ?? [])
      .filter(policy => policy.permitted && policy?.inputs?.length)
      .flatMap(
        policy =>
          (policy.inputs ?? []).map(input => {
            const modificationPolicy: OrderCancelPolicy = {
              type: 'cancel',
              label: input.label!,
              reason: input.customerReason!,
              cancellationType: input.reasonType!,
              fee: convertMoney(policy.permitted?.cancelFee ?? {}),
            }
            return modificationPolicy
          }) ?? []
      ),
    reschedulePolicies: (policyList.reschedulePolicies ?? [])
      .filter(policy => policy.permitted && policy?.inputs?.length)
      .flatMap(policy =>
        (policy.inputs ?? []).map(input => {
          const modificationPolicy: OrderReschedulePolicy = {
            type: 'reschedule',
            label: input.label!,
            reason: input.reason!,
            fee: convertMoney(policy.permitted?.rescheduleFee ?? {}),
          }
          return modificationPolicy
        })
      ),
  }
}

export const useGetOrderChangePolicyList = (
  params: GetOrderChangePolicyListRequestParams
): EagerResponse<OrderChangePolicyList> => {
  const query = useGetOrderChangePolicyListQuery({
    variables: {
      requestContext: params.requestContext!,
      orderDeliveryId: params.orderDeliveryId!,
      userId: params.userId!,
      retailerAppId: params.retailerAppId!,
    },
    fetchPolicy: 'cache-and-network',
    refetchWritePolicy: 'overwrite',
    skip:
      isEmpty(params.orderDeliveryId) ||
      isEmpty(params.userId) ||
      isEmpty(params.retailerAppId) ||
      isEmpty(params.requestContext),
  })

  return useApolloRequestWrapper({ query, mapResponse })
}
